import { CalendarDays, Clock3, RefreshCw, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { inputCls } from "@/components/FormBits";
import { api, formatApiError } from "@/lib/api";
import { formatCurrency, todayForDateInput } from "@/lib/goals";

const deadlineOptions = [
  ["none", "Sem prazo", "Vou guardar no meu ritmo.", Sparkles],
  ["date", "Escolher uma data", "Quero chegar lá até uma data.", CalendarDays],
  ["days", "Quantidade de dias", "Quero definir quantos dias tenho.", Clock3],
];

export const DeadlineRecalculateModal = ({ goal, onClose, onUpdated }) => {
  const [mode, setMode] = useState(goal.deadline_mode || "none");
  const [date, setDate] = useState(goal.deadline_date || "");
  const [days, setDays] = useState(goal.deadline_days ? String(goal.deadline_days) : "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const selectedDate = date ? new Date(`${date}T00:00:00`) : null;
  const localToday = new Date();
  localToday.setHours(0, 0, 0, 0);
  const plannedSquares = mode === "days"
    ? Number(days) || 0
    : mode === "date" && selectedDate
      ? Math.max(Math.round((selectedDate - localToday) / 86400000), 1)
      : 100;

  const recalculate = async () => {
    setError("");
    if (mode === "date" && !date) {
      setError("Escolha uma data para o novo prazo.");
      return;
    }
    if (mode === "days" && Number(days) < 1) {
      setError("Informe uma quantidade de dias válida.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/goals/${goal.id}/deadline`, {
        deadline_mode: mode,
        deadline_date: mode === "date" ? date : null,
        deadline_days: mode === "days" ? Number(days) : null,
      });
      onUpdated(data);
      toast.success("Prazo atualizado e quadradinhos recalculados.");
      onClose();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="deadline-recalculate-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deadline-recalculate-title"
      className="savings-backdrop"
    >
      <section className="savings-modal">
        <button
          type="button"
          onClick={onClose}
          data-testid="deadline-recalculate-close-button"
          className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full text-emerald-950 hover:bg-emerald-100"
          aria-label="Fechar recálculo"
        >
          <X className="h-6 w-6" />
        </button>
        <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Ajustar planejamento</p>
        <h2
          id="deadline-recalculate-title"
          className="mt-2 font-heading text-3xl font-extrabold text-emerald-950"
        >
          Refazer cálculo do prazo
        </h2>
        <p className="mt-3 text-lg leading-relaxed text-slate-700">
          Sua meta continua em <strong>{formatCurrency(goal.amount_cents)}</strong>.
          Vamos criar uma nova distribuição de quadradinhos que sempre soma esse total.
        </p>
        <p data-testid="deadline-grid-preview" className="mt-3 rounded-xl bg-emerald-50 p-3 text-base font-bold text-emerald-950">
          {mode === "none"
            ? "Sem prazo, seu cofre terá 100 quadradinhos."
            : `${plannedSquares || "—"} quadradinho${plannedSquares === 1 ? "" : "s"}: um para cada dia.`}
        </p>
        <div className="mt-7 grid gap-3">
          {deadlineOptions.map(([value, label, description, Icon]) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              data-testid={`recalculate-deadline-mode-${value}`}
              className={`flex min-h-[74px] items-center gap-4 rounded-xl border-2 p-4 text-left transition-[transform,border-color,background-color] hover:-translate-y-0.5 ${mode === value ? "border-emerald-700 bg-emerald-50" : "border-amber-200 bg-white hover:border-amber-400"}`}
            >
              <Icon className="h-7 w-7 shrink-0 text-amber-700" />
              <span>
                <span className="block text-lg font-bold text-emerald-950">{label}</span>
                <span className="block text-base text-slate-600">{description}</span>
              </span>
            </button>
          ))}
        </div>
        {mode === "date" && (
          <label className="mt-6 block text-lg font-bold text-slate-800" htmlFor="recalculate-deadline-date">
            Nova data limite
            <input
              id="recalculate-deadline-date"
              data-testid="recalculate-deadline-date-input"
              type="date"
              min={todayForDateInput()}
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className={`mt-2 ${inputCls}`}
            />
          </label>
        )}
        {mode === "days" && (
          <label className="mt-6 block text-lg font-bold text-slate-800" htmlFor="recalculate-deadline-days">
            Quantos dias você quer?
            <input
              id="recalculate-deadline-days"
              data-testid="recalculate-deadline-days-input"
              type="number"
              min="1"
              max="365"
              value={days}
              onChange={(event) => setDays(event.target.value)}
              inputMode="numeric"
              placeholder="Ex.: 7"
              className={`mt-2 ${inputCls}`}
            />
          </label>
        )}
        {error && <p data-testid="deadline-recalculate-error" role="alert" className="mt-5 rounded-xl border-2 border-red-200 bg-red-50 p-4 text-lg text-red-800">{error}</p>}
        <button
          type="button"
          onClick={recalculate}
          disabled={saving}
          data-testid="deadline-recalculate-submit-button"
          className="savings-confirm-button mt-7 disabled:opacity-60"
        >
          <RefreshCw className="h-5 w-5" />
          {saving ? "Recalculando..." : "Recalcular quadradinhos"}
        </button>
      </section>
    </div>
  );
};